import { Link } from 'react-router-dom'

const CaseStudies = () => {
  const caseStudies = [
    {
      id: 1,
      title: "Solar Installation in Surrey",
      location: "Surrey, UK",
      type: "Solar Panels",
      image: null, // TODO: Add case study images
      description: "A comprehensive 6kW solar panel installation that reduced the household's electricity bills by 70%.",
      results: [
        "70% reduction in electricity bills",
        "£1,200 annual savings",
        "4.2-year payback period",
        "5.2 tonnes CO2 saved annually"
      ],
      testimonial: {
        quote: "Gaia Powered made the whole process seamless. Our energy bills have dropped dramatically, and we're doing our part for the environment.",
        author: "Sarah Johnson",
        role: "Homeowner"
      }
    },
    {
      id: 2,
      title: "Air Source Heat Pump in Manchester",
      location: "Manchester, UK",
      type: "Heat Pump",
      image: null,
      description: "Replaced an aging gas boiler with a modern air source heat pump system, achieving significant cost savings.",
      results: [
        "£800 annual heating cost reduction",
        "300% efficiency vs old boiler",
        "Eligible for £7,500 government grant",
        "3.8 tonnes CO2 saved annually"
      ],
      testimonial: {
        quote: "We were nervous about moving away from gas heating, but the heat pump has exceeded our expectations. The house stays warm, and the running costs are much lower.",
        author: "Michael Chen",
        role: "Homeowner"
      }
    },
    {
      id: 3,
      title: "EV Charger Installation in Bristol",
      location: "Bristol, UK",
      type: "EV Charging",
      image: null,
      description: "Smart EV charger installation for a family with two electric vehicles, integrated with existing solar panels.",
      results: [
        "90% charging from solar energy",
        "£1,000 annual fuel savings",
        "7kW fast charging capability",
        "Smart scheduling for off-peak rates"
      ],
      testimonial: {
        quote: "Charging our EVs with solar power feels amazing. We've virtually eliminated our fuel costs and our carbon footprint.",
        author: "Emma Thompson",
        role: "Homeowner"
      }
    },
    {
      id: 4,
      title: "Complete Home Energy Solution in London",
      location: "London, UK",
      type: "Solar + Heat Pump + EV Charger",
      image: null,
      description: "Full renewable energy transformation including solar panels, heat pump, and EV charger for a Victorian terrace.",
      results: [
        "85% reduction in energy bills",
        "£2,500 annual savings",
        "EPC rating improved from D to A",
        "8.5 tonnes CO2 saved annually"
      ],
      testimonial: {
        quote: "Gaia Powered transformed our home into an eco-friendly powerhouse. The comprehensive approach meant everything works together perfectly.",
        author: "David & Lisa Martinez",
        role: "Homeowners"
      }
    },
    {
      id: 5,
      title: "Air Conditioning for Modern Office",
      location: "Birmingham, UK",
      type: "Air Conditioning",
      image: null,
      description: "Multi-zone air conditioning system for a small business office, providing year-round climate control.",
      results: [
        "40% reduction in energy costs",
        "Improved employee comfort",
        "Heating and cooling in one system",
        "Smart temperature control"
      ],
      testimonial: {
        quote: "The new air conditioning system has made a huge difference to our office environment. Staff are more comfortable, and our energy bills have actually gone down.",
        author: "Robert Williams",
        role: "Business Owner"
      }
    },
    {
      id: 6,
      title: "Solar + Battery Storage in Edinburgh",
      location: "Edinburgh, UK",
      type: "Solar Panels + Battery",
      image: null,
      description: "8kW solar installation with 10kWh battery storage, maximizing energy independence.",
      results: [
        "95% energy independence",
        "£1,800 annual savings",
        "Power during outages",
        "6.1 tonnes CO2 saved annually"
      ],
      testimonial: {
        quote: "Having battery storage means we use almost all the energy we generate. We're practically off the grid now!",
        author: "Fiona MacLeod",
        role: "Homeowner"
      }
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-green to-secondary-green text-white py-20 md:py-32">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-poppins font-bold mb-6">
              Case Studies
            </h1>
            <p className="text-xl md:text-2xl text-green-50">
              Real results from real customers across the UK
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-lg text-gray-700">
              See how Gaia Powered has helped homeowners and businesses across the UK
              reduce their energy costs, lower their carbon footprint, and embrace
              sustainable living. Each case study showcases real installations,
              measurable results, and genuine customer feedback.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="pb-16 md:pb-24">
        <div className="container-custom">
          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <div
                key={study.id}
                className={`bg-gray-50 rounded-lg overflow-hidden shadow-lg ${
                  index % 2 === 0 ? 'md:grid md:grid-cols-2' : 'md:grid md:grid-cols-2'
                }`}
              >
                {/* Content */}
                <div className={`p-8 md:p-12 ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                  <div className="inline-block bg-primary-green text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                    {study.type}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-poppins font-bold text-dark-plum mb-2">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {study.location}
                  </p>
                  <p className="text-gray-700 mb-6">
                    {study.description}
                  </p>

                  <h4 className="font-semibold text-dark-plum mb-3">Key Results:</h4>
                  <ul className="space-y-2 mb-6">
                    {study.results.map((result, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-primary-green mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{result}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="border-l-4 border-primary-green bg-white p-4 rounded">
                    <p className="text-gray-700 italic mb-2">"{study.testimonial.quote}"</p>
                    <p className="text-sm font-semibold text-dark-plum">
                      - {study.testimonial.author}, {study.testimonial.role}
                    </p>
                  </div>
                </div>

                {/* Image Placeholder */}
                <div className={`bg-gradient-to-br from-primary-green to-secondary-green flex items-center justify-center min-h-[300px] ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <div className="text-white text-center p-8">
                    <svg className="w-24 h-24 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm opacity-75">Image coming soon</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary-green to-secondary-green text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold mb-6">
            Ready to Create Your Own Success Story?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our growing list of satisfied customers who are saving money and
            helping the environment with renewable energy solutions.
          </p>
          <Link
            to="/quote"
            className="inline-block bg-white text-primary-green px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors mr-4 mb-4"
          >
            Get Your Free Quote
          </Link>
          <Link
            to="/contact"
            className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-green transition-colors mb-4"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  )
}

export default CaseStudies
